import {
  AutoAwesomeRounded,
  AutoStoriesRounded,
  CandlestickChartRounded,
  DrawRounded,
  ExtensionRounded,
  GitHub,
  QueryStatsRounded,
  Twitter,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Container,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { animated, useTransition } from "@react-spring/web"
import { useQuery } from "@tanstack/react-query"
import React, { useEffect, useMemo, useState } from "react"
import { CoinGeckoSvg } from "src/components/CoingeckoSvg"
import { Accordion, AccordionDetails, AccordionSummary } from "src/components/CustomAccordion"
import { DiscordSvg } from "src/components/DiscordSvg"
import { Footer } from "src/components/Footer"
import { FramerImage } from "src/components/FramerImage"
import { AlchemySvg } from "src/components/icons/AlchemySvg"
import { BinanceSvg } from "src/components/icons/BinanceSvg"
import { CoinbaseSvg } from "src/components/icons/CoinbaseSvg"
import { EtherscanSvg } from "src/components/icons/EtherscanSvg"
import { LongTermRoadmap } from "src/components/LongTermRoadmap"
import { MyCard, MyCardDescription, MyCardTitle } from "src/components/MyCard"
import { StaggeredList } from "src/components/StaggeredList"
import { UnderTheHood } from "src/components/UnderTheHood"
import { getCoingeckoStats } from "src/utils/coingecko-api"
import {
  getIconComponent,
  getPlatformDownloads,
  selectPlatformDownload,
} from "src/utils/github-api"
import { isServer, SPRING_CONFIGS } from "src/utils/utils"

import { Subheading } from "../components/Subheading"
import { UnderTitle } from "../components/UnderTitle"

// Import optimized images using vite-imagetools with import.meta.glob
// Format: webp for better compression, width: 700px for 2x retina display in phone frame
const imageModules = import.meta.glob("./landing/demo/*.png", {
  eager: true,
  import: "default",
  query: { format: "webp", w: 700 },
})

// Extract images in specific order
const demoImages = [
  imageModules["./landing/demo/pnl_measure.png"],
  imageModules["./landing/demo/pnl_1.png"],
  imageModules["./landing/demo/pnl_2.png"],
  imageModules["./landing/demo/breakdown.png"],
  imageModules["./landing/demo/nw_1.png"],
  imageModules["./landing/demo/nw_2.png"],
] as string[]

export default function LandingPage() {
  const [imageIndex, setImageIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndex((state) => (state + 1) % demoImages.length)
    }, 5_000)
    return () => clearInterval(interval)
  }, [])

  const imageTransitions = useTransition(imageIndex, {
    config: { duration: 300 },
    enter: { opacity: 1 },
    from: { opacity: 0 },
    leave: { opacity: 1 },
  })

  const { data: stats } = useQuery({
    queryFn: getCoingeckoStats,
    queryKey: ["coingecko-stats"],
  })

  const { data: allDownloads } = useQuery({
    queryFn: getPlatformDownloads,
    queryKey: ["platformDownloads"],
  })

  const download = useMemo(() => {
    if (!allDownloads || allDownloads.length === 0) return null
    return selectPlatformDownload(allDownloads)
  }, [allDownloads])

  return (
    <>
      <StaggeredList gap={6} config={SPRING_CONFIGS.quick} delay={200}>
        <StaggeredList gap={2} alignItems="center" config={SPRING_CONFIGS.slow} delay={600}>
          <Subheading>
            The AI <Box component="br" sx={{ display: { sm: "none" } }} /> Wealth Manager
          </Subheading>
          <UnderTitle>A free* and open-source toolkit for financial empowerment</UnderTitle>
        </StaggeredList>
        <Stack
          paddingX={1}
          direction={{ sm: "row", xs: "column" }}
          justifyContent="center"
          alignItems="center"
          gap={{ sm: 2 }}
        >
          <Box
            sx={{
              border: "15px solid rgba(66, 66, 66, 1)",
              borderRadius: "40px",
              height: { lg: 627, xs: 550 },
              "html.dark &": {
                border: "15px solid rgba(80, 80, 80, 0.5)",
              },
              marginBottom: { lg: "-128px", xs: "-190px" },
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 80%)",
              position: "relative",
              width: { lg: 305, xs: 280 },
            }}
          >
            {isServer && (
              <img
                alt="Demo of the app"
                src={demoImages[0]}
                style={{
                  borderRadius: "25px",
                  height: "auto",
                  left: 0,
                  position: "absolute",
                  top: 0,
                  width: "100%",
                }}
              />
            )}
            {imageTransitions((style, i) => (
              <animated.img
                src={demoImages[i]}
                style={{
                  ...style,
                  borderRadius: "25px",
                  height: "auto",
                  left: 0,
                  position: "absolute",
                  top: 0,
                  width: "100%",
                }}
              />
            ))}
          </Box>
          <Typography
            color="text.secondary"
            variant="body2"
            component={Paper}
            elevation={1}
            sx={{
              background: { sm: "transparent !important" },
              border: { sm: 0 },
              boxShadow: "none !important",
              display: "flex",
              maxWidth: { sm: 292, xs: 445 },
              minHeight: { sm: 0, xs: 400 },
              padding: { md: 2, sm: 0, xs: 2 },
            }}
          >
            <Stack gap={3} justifyContent="center">
              <Stack direction="row" gap={2} alignItems="center">
                <QueryStatsRounded fontSize="large" />
                Time travel through trades to surface the strengths and weaknesses of your portfolio
              </Stack>
              <Stack direction="row" gap={2} alignItems="center">
                <CandlestickChartRounded fontSize="large" />
                Inspect and compare your historical net worth with no limitations
              </Stack>
              <Stack direction="row" gap={2} alignItems="center">
                <DrawRounded fontSize="large" />
                Use advanced TradingView features on your data like indicators, drawing tools, etc.
              </Stack>
              <Stack direction="row" gap={2} alignItems="center">
                <AutoAwesomeRounded fontSize="large" />
                Prepare your tax report or feed data into your AI assistant
              </Stack>
              <Stack direction="row" gap={2} alignItems="center">
                <ExtensionRounded fontSize="large" />
                <span>
                  Extend the project under the <b>AGPL-3.0</b> license
                </span>
              </Stack>
            </Stack>
          </Typography>
        </Stack>
        <Stack alignItems="center" gap={0.5}>
          <Button
            href={download?.url || "https://privatefolio.app"}
            target="_blank"
            variant="contained"
            color="primary"
            sx={{
              "& svg": {
                fontSize: "2rem !important",
              },
              "&:hover": {
                transform: "translateY(-1px)",
              },
              background: "linear-gradient(-25deg, rgb(230, 180, 252) 0%, rgb(135, 21, 173) 100%)",
              borderRadius: 10,
              gap: 0.5,
              "html.dark &": {
                background:
                  "linear-gradient(-25deg, rgba(210, 160, 252, 1) 0%, rgb(255, 255, 255) 100%)",
              },
              marginTop: 2,
              paddingX: 6,
              paddingY: 1.5,
              transition: "transform 0.1s ease-in-out",
            }}
            startIcon={download?.icon ? getIconComponent(download.icon, download.iconStyle) : null}
            disableElevation
          >
            <Typography component="span" variant="h5" fontWeight={600}>
              {download?.label || "Open web app"}
            </Typography>
            {/* {!download && (
              <ArrowOutwardRounded
                sx={{
                  ".MuiButton-root &": {
                    fontSize: "1.25rem !important",
                  },
                }}
              />
            )} */}
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            *Free as in Freedom
          </Typography>
        </Stack>
        <Stack marginTop={16} gap={12}>
          <UnderTheHood />
          <MyCard variant="transparent">
            <FramerImage
              id="juH3gG75mErF93kaXE68cRUTlGI"
              sx={{
                "@keyframes scale": {
                  from: { transform: "scale(0.95)" },
                  to: { transform: "scale(1)" },
                },
                animation: "scale 1.5s ease-in-out infinite alternate",
              }}
            />
            <Stack gap={3} alignItems="center">
              <MyCardTitle id="integrations">Diverse APIs integrations</MyCardTitle>
              <MyCardDescription>
                Query prices from our{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/coinbase-price-api"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<CoinbaseSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Coinbase
                </Button>
                ,{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/binance-price-api"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<BinanceSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Binance
                </Button>{" "}
                and{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/alchemy-price-api"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<AlchemySvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Alchemy
                </Button>{" "}
                adapters. <br /> Retrieve your transaction history using{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/etherscan-connection"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<EtherscanSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Etherscan
                </Button>{" "}
                or{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/binance-file-import"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<BinanceSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Binance
                </Button>
                .
                <br />
                Discover more in our extensions store. <br />
                <Box marginTop={4}>
                  Are you a developer? Check out our{" "}
                  <Button
                    href="https://github.com/privatefolio/privatefolio?tab=readme-ov-file#for-developers"
                    target="_blank"
                    color="primary"
                    size="small"
                    startIcon={<GitHub width={14} height={14} />}
                    sx={{
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                      borderRadius: 10,
                      "html.dark &": {
                        background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                      },
                      marginY: -1,
                      paddingX: 0.75,
                      paddingY: 0.25,
                    }}
                  >
                    developer guide on GitHub
                  </Button>
                  .
                </Box>
              </MyCardDescription>
            </Stack>
          </MyCard>
          <MyCard variant="colorful" direction="row-reverse">
            <FramerImage
              id="mS28QhWcSped7JSQ7vltnisa5c8"
              sx={{
                "@keyframes rotate": {
                  from: { transform: "rotate(0deg) rotateY(180deg)" },
                  to: { transform: "rotate(5deg) rotateY(180deg)" },
                },
                animation: "rotate 1.5s ease-in-out infinite alternate",
              }}
            />
            <Stack gap={3} alignItems="center">
              <MyCardTitle id="coingecko">First-class Coingecko integration</MyCardTitle>
              <MyCardDescription>
                Explore {stats?.coins} digital assets, {stats?.exchanges} exchanges and <br />{" "}
                {stats?.blockchains} blockchains with our{" "}
                <Button
                  href="https://1.privatefolio.app/l/0/extension/coingecko-metadata"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<CoinGeckoSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Coingecko
                </Button>{" "}
                extension .
              </MyCardDescription>
            </Stack>
          </MyCard>
          <LongTermRoadmap />
          <MyCard variant="transparent">
            <FramerImage
              id="TMSU7EEn6tWu1bXQjXJoGO6p1rA"
              sx={{
                "@keyframes translate": {
                  from: { transform: "translateY(0px)" },
                  to: { transform: "translateY(15px)" },
                },
                animation: "translate 1.5s ease-in-out infinite alternate",
              }}
            />
            <Stack gap={3} alignItems="center">
              <MyCardTitle id="get-involved">Get involved</MyCardTitle>
              <MyCardDescription>Find us on Twitter, Discord or GitHub.</MyCardDescription>
              <Stack direction="row">
                <IconButton
                  size="large"
                  target="_blank"
                  href="https://twitter.com/PrivatefolioApp"
                  LinkComponent={MuiLink}
                  color="primary"
                >
                  <Twitter fontSize="large" />
                </IconButton>
                <IconButton
                  size="large"
                  target="_blank"
                  href="https://discord.gg/YHHu9nK8VD"
                  LinkComponent={MuiLink}
                  color="primary"
                >
                  <DiscordSvg />
                </IconButton>
                <IconButton
                  size="large"
                  target="_blank"
                  href="https://github.com/kernelwhisperer/privatefolio"
                  LinkComponent={MuiLink}
                  color="primary"
                >
                  <GitHub fontSize="large" />
                </IconButton>
              </Stack>
            </Stack>
          </MyCard>
          <MyCard variant="colorful" direction="row-reverse">
            <FramerImage id="U8BliLSYurKQHRyLYQC9YGr3sHU" sx={{ transform: "rotateY(180deg)" }} />
            <Stack gap={3} alignItems="center">
              <MyCardTitle id="newsletter">Join our newsletter</MyCardTitle>
              <MyCardDescription>Keep up with the latest updates and news.</MyCardDescription>
              <Box
                component="iframe"
                src="https://paragraph.xyz/@privatefolio/embed?minimal=true&vertical=true"
                sx={{
                  background: "white",
                  height: 136,
                  "html.dark &": {
                    background: "#12120c",
                  },
                  width: 260,
                }}
                frameBorder="0"
                scrolling="no"
              />
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Or read it first at{" "}
                <MuiLink
                  href="https://paragraph.xyz/@privatefolio"
                  target="_blank"
                  underline="hover"
                >
                  paragraph.xyz/@privatefolio
                </MuiLink>
                .
              </Typography>
            </Stack>
          </MyCard>
        </Stack>
        <Container maxWidth="sm" sx={{ marginTop: 4 }}>
          <MyCardTitle id="faq" marginBottom={2}>
            Frequently Asked Questions
          </MyCardTitle>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>Is Privatefolio really free?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Yes! Privatefolio is completely free to use, free to modify and free to distribute,
                its source code is available under the <b>AGPL-3.0</b> license. However, since
                Privatefolio is extensible, just like in App Store or Google Play, you may encounter
                extensions that require a one-time fee or a paid subscription.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>Can I self-host Privatefolio?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Yes! The easiest way to self-host is to either install any of the{" "}
                <MuiLink href="/apps">desktop apps</MuiLink> on your computer, or to
                <Button
                  href="https://docs.privatefolio.app/self-hosting-with-docker"
                  target="_blank"
                  color="primary"
                  size="small"
                  endIcon={<AutoStoriesRounded width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  run the Docker image
                </Button>{" "}
                on your server .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>Can I contribute to the project?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Absolutely! Open-source and collaboration are at the core of our mission. You can
                develop custom extensions and earn money doing so. Extensions can range from support
                for new csv formats, new price providers, advanced analytics, AI features (like
                voice, or deep research agents) and much more.
                <br />
                You can provide suggestions on how we can improve the core app, or help with
                documentation. Check out our{" "}
                <Button
                  href="https://github.com/privatefolio/privatefolio?tab=readme-ov-file#for-developers"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<GitHub width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  developer quickstart on GitHub
                </Button>{" "}
                and make sure to join our{" "}
                <Button
                  href="https://discord.gg/YHHu9nK8VD"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<DiscordSvg width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  Discord server
                </Button>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>Is there a premium plan?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Yes! The app is free, but we offer a premium plan that includes a faster cloud
                machine, higher AI limits and priority support. This helps us maintain and improve
                the project.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>Is Privatefolio like WordPress?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Exactly! Like WordPress, Privatefolio is open-source, self-hostable, and highly
                extensible. Anyone can develop extensions (i.e. plugins) to add new features.
                Similarly to WordPress&apos;s business model, we offer a premium plan that includes
                a faster cloud machine, higher AI limits and priority support.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary>
              <Typography fontWeight={500}>What is the AGPL-3.0 license?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography component="div" color="text.secondary">
                The AGPL-3.0 (GNU Affero General Public License) is a strong copyleft license that
                ensures the software remains free and open. Here are the key freedoms it provides:
                <ul>
                  <li>
                    <b>Freedom to Use</b>: You can use the software for any purpose, including
                    commercial use.
                  </li>
                  <li>
                    <b>Freedom to Study and Modify</b>: You can study how the program works and
                    modify it to suit your needs, including for commercial purposes.
                  </li>
                  <li>
                    <b>Freedom to Distribute Copies</b>: You can redistribute copies of the original
                    program.
                  </li>
                  <li>
                    <b>Freedom to Distribute Modified Versions</b>: You are allowed to distribute
                    your modified versions, including for profit. However, if you run a modified
                    version of the software on a server and allow others to interact with it, you
                    must provide the source code of the modified version to those users.
                  </li>
                </ul>
                This helps maintain the open ecosystem while allowing commercial use through our
                premium offerings. <br />
                Learn more by reading our{" "}
                <Button
                  href="https://github.com/privatefolio/privatefolio/blob/main/LICENSE"
                  target="_blank"
                  color="primary"
                  size="small"
                  startIcon={<GitHub width={14} height={14} />}
                  sx={{
                    background: "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
                    borderRadius: 10,
                    "html.dark &": {
                      background: "rgba(var(--mui-palette-primary-mainChannel) / 0.15)",
                    },
                    marginY: -1,
                    paddingX: 0.75,
                    paddingY: 0.25,
                  }}
                >
                  license on GitHub
                </Button>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Container>
      </StaggeredList>
      <Footer />
    </>
  )
}
